import {
  Grid,
  Link,
  Switch,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Stack,
} from "@mui/material";
import * as React from "react";
import { AuthenticationContext } from "../Context";
import FlashMessage from "../shared/FlashMessage";
import { getHeaders } from "../utils/HeaderHelper";
import DeleteIcon from "@mui/icons-material/Delete";
import ArchiveIcon from "@mui/icons-material/Archive";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CreateIcon from "@mui/icons-material/Create";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import moment from "moment/moment";
import Modal from "../modals/Modal";
import { useHistory } from "react-router-dom";
import { useFlash } from "../FlashContext";

const modalContent = (
  <div className="modal-content">
    <p className="align-center">
      You are attempting to delete this claim template. This action cannot be
      undone. Would you like to continue?
    </p>
  </div>
);

const ClaimTemplateBuilder: React.FC = (props: any) => {
  const authenticationSetting = React.useContext(AuthenticationContext);
  const { showToast } = useFlash();
  const history = useHistory();

  const [showArchiveModal, setShowArchiveModal] =
    React.useState<boolean>(false);
  const [showPublishModal, setShowPublishModal] =
    React.useState<boolean>(false);
  const [deleteProgram, setDeleteProgram] = React.useState<any>();
  const [publishProgramId, setPublishProgramId] = React.useState<string>("");
  const [allPrograms, setAllPrograms] = React.useState<any>([]);
  const [showArchived, setShowArchived] = React.useState<boolean>(false);
  const [showClaimTemplateDeleteModal, setShowClaimTemplateDeleteModal] =
    React.useState<boolean>(false);
  const [selectedClaimTemplate, setSelectedClaimTemplate] =
    React.useState<any>(null);
  const [searchKey, setSearchKey] = React.useState<string>("");
  const [claimTemplates, setClaimTemplates] = React.useState([]);
  const [sortObject, setSortObject] = React.useState<any>({
    field: "category",
    direction: "ascending",
  });
  const authenticationSettings = React.useContext(AuthenticationContext);
  const { currentUser, ..._ } = authenticationSettings;

  React.useEffect(() => {
    fetchClaimTemplates();
  }, [showArchived]);

  const fetchClaimTemplates = () => {
    fetch(`/claim_templates?show_archived=${showArchived}`, {
      method: "GET",
      headers: getHeaders(authenticationSetting.csrfToken),
    })
      .then((result) => result.json())
      .then((result) => {
        if (!result.success) {
          console.log(result.error);
        } else {
          setClaimTemplates(result.resource);
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const publishClaimTemplate = async (claimTemplateId) => {
    try {
      const response = await fetch(
        `/claim_templates/${claimTemplateId}/publish`,
        {
          method: "PATCH",
          headers: getHeaders(authenticationSetting.csrfToken),
        }
      );

      const result = await response.json();

      if (result.success) {
        showToast({
          title: "Claim Template Published",
          text: "Claim template published successfully",
          type: "success",
        });
        fetchClaimTemplates();
      } else {
        showToast({
          title: "Error",
          text: "Something went wrong",
          type: "error",
        });
      }
    } catch (error) {
      showToast({
        title: "Failed to Publish",
        text: error.message ?? "Failed to publish claim template",
        type: "error",
      });
    } finally {
    }
  };

  const archiveClaimTemplate = async (claimTemplateId) => {
    try {
      const response = await fetch(
        `/claim_templates/${claimTemplateId}/archive`,
        {
          method: "PATCH",
          headers: getHeaders(authenticationSetting.csrfToken),
        }
      );

      const result = await response.json();

      if (result.success) {
        showToast({
          title: "Claim Template Archived",
          text: "Claim template archived successfully",
          type: "success",
        });
        fetchClaimTemplates();
      } else {
        showToast({
          title: "Error",
          text: "Something went wrong",
          type: "error",
        });
      }
    } catch (error) {
      showToast({
        title: "Failed to Archive",
        text: error.message ?? "Failed to archive claim template",
        type: "error",
      });
    } finally {
    }
  };

  const deleteClaimTemplate = async () => {
    try {
      const response = await fetch(
        `/claim_templates/${selectedClaimTemplate.id}`,
        {
          method: "DELETE",
          headers: getHeaders(authenticationSetting.csrfToken),
        }
      );

      const result = await response.json();

      if (result.success) {
        showToast({
          title: "Claim Template Deleted",
          text: "Claim template deleted successfully",
          type: "success",
        });
        fetchClaimTemplates();
      } else {
        showToast({
          title: "Error",
          text: "Something went wrong",
          type: "error",
        });
      }
    } catch (error) {
      showToast({
        title: "Failed to Delete",
        text: error.message ?? "Failed to delete claim template",
        type: "error",
      });
    } finally {
      closeClaimTemplateDeleteModal();
    }
  };

  const handleShowArchieved = () => {
    setShowArchived(!showArchived);
  };

  React.useEffect(() => {
    if (deleteProgram?.id) {
      setShowArchiveModal(true);
    }
  }, [deleteProgram?.id]);

  React.useEffect(() => {
    if (publishProgramId) {
      setShowPublishModal(true);
    }
  }, [publishProgramId]);

  const openClaimTemplateDeleteModal = (claimTemplate) => {
    setSelectedClaimTemplate(claimTemplate);
    setShowClaimTemplateDeleteModal(true);
  };

  const closeClaimTemplateDeleteModal = () => {
    setSelectedClaimTemplate(null);
    setShowClaimTemplateDeleteModal(false);
  };

  const handleEditClaimTemplate = (claimTemplateId) => {
    history.push(`add-claim-template/${claimTemplateId}`);
  };

  const handleViewClaimTemplate = (claimTemplateId) => {
    history.push(`add-claim-template/${claimTemplateId}?view=true`);
  };

  return (
    <div className="main-content-outer">
      <Grid
        container
        direction="row"
        justifyContent="flex-start"
        alignItems="flex-start"
        className="main-content"
        sx={{ paddingLeft: "138px" }}
      >
        <Grid
          item
          xs={12}
          className="patient-edit-container patient-edit-form"
          sx={{ paddingBottom: "0px" }}
        >
          <Grid container>
            <Grid
              className="patient-edit-header"
              container
              justifyContent="space-between"
            >
              <Grid item xs={4}>
                <Stack direction="row" columnGap="12px" alignItems="center">
                  <Link
                    style={{
                      textDecoration: "none",
                      display: "flex",
                      alignItems: "center",
                    }}
                    href={`/claim-builder`}
                  >
                    <ArrowBackIosNewIcon
                      style={{
                        borderRadius: "3px",
                        padding: "8px",
                        color: "black",
                        boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.2)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        transition: "all 0.3s ease",
                        fontSize: "14px",
                      }}
                    />
                  </Link>
                  <p className="secondary-label" style={{ marginLeft: "0px" }}>
                    Claim Template Builder
                  </p>
                </Stack>
              </Grid>

              <Grid item xs={6} className="q-btn-container">
                <span className="font-16px grey-font">
                  <Switch
                    checked={showArchived}
                    onChange={handleShowArchieved}
                    color="primary"
                  />
                  Show Archived Templates
                </span>

                <Link
                  href="/add-claim-template"
                  className="grey-font"
                  style={{
                    height: "40px",
                    marginRight: "16px",
                    paddingLeft: "16px",
                    paddingRight: "16px",
                    display: "flex",
                    alignItems: "center",
                    fontSize: "16px",
                    position: "relative",
                    top: "-2px",
                  }}
                >
                  <img
                    src="https://starfield-static-assets.s3.us-east-2.amazonaws.com/menu-track.png"
                    width="40"
                    alt="Add New Program"
                    style={{ padding: "5px" }}
                  />
                  Add Claim Template
                </Link>
              </Grid>
            </Grid>
            <Grid container>
              <Grid
                item
                xs={12}
                className="form-container"
                sx={{ paddingBottom: "0px !important" }}
              >
                {claimTemplates?.length === 0 ? (
                  <p
                    style={{
                      color: "#1E1E1E",
                      textAlign: "center",
                      fontSize: "13px",
                      fontStyle: "normal",
                      fontWeight: 500,
                      lineHeight: "18px",
                    }}
                  >
                    You have not created a Claim Template yet. You can create
                    your first Claim Template{" "}
                    <span
                      style={{
                        color: "#3F51B5",
                        textDecorationLine: "underline",
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        history.push("/add-claim-template");
                      }}
                    >
                      here!
                    </span>
                  </p>
                ) : (
                  <TableContainer style={{ maxHeight: "calc(80vh - 200px)" }}>
                    <Table stickyHeader aria-label="a dense table">
                      <TableHead
                        sx={{
                          boxShadow: "0px 4px 4px 0px #00000040",
                          backgroundColor: "#fff",
                          position: "sticky",
                          top: 0,
                          zIndex: 1,
                        }}
                      >
                        <TableRow
                          sx={{
                            "& td, & th": {
                              borderBottom: "none",
                            },
                          }}
                        >
                          <TableCell align="left" sx={{ width: "250px" }}>
                            Title
                          </TableCell>
                          <TableCell align="left" sx={{ minWidth: "120px" }}>
                            Subtext
                          </TableCell>
                          <TableCell align="center" sx={{ width: "100px" }}>
                            Date Published
                          </TableCell>
                          <TableCell align="center" sx={{ width: "67px" }}>
                            Edit/View
                          </TableCell>
                          <TableCell align="center" sx={{ width: "53px" }}>
                            Archive
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {claimTemplates?.map((claimTemplate, index) => (
                          <TableRow
                            sx={{ "& td, & th": { borderBottom: "none" } }}
                            key={index + 1}
                          >
                            <TableCell align="left">
                              {claimTemplate.title}
                            </TableCell>
                            <TableCell align="left">
                              {claimTemplate.subtext}
                            </TableCell>
                            <TableCell align="center">
                              {claimTemplate.status === "published" ? (
                                moment(claimTemplate.date_published).format(
                                  "MM/DD/YYYY"
                                )
                              ) : (
                                <Button
                                  onClick={() =>
                                    publishClaimTemplate(claimTemplate.id)
                                  }
                                >
                                  Publish
                                </Button>
                              )}
                            </TableCell>
                            <TableCell align="center">
                              {claimTemplate.status === "draft" ? (
                                <CreateIcon
                                  style={{
                                    cursor: "pointer",
                                  }}
                                  onClick={() =>
                                    handleEditClaimTemplate(claimTemplate.id)
                                  }
                                />
                              ) : (
                                <VisibilityIcon
                                  style={{
                                    cursor: "pointer",
                                  }}
                                  onClick={() =>
                                    handleViewClaimTemplate(claimTemplate.id)
                                  }
                                />
                              )}
                            </TableCell>
                            <TableCell align="center">
                              {claimTemplate.status === "draft" ? (
                                <DeleteIcon
                                  style={{
                                    cursor: "pointer",
                                  }}
                                  onClick={() =>
                                    openClaimTemplateDeleteModal(claimTemplate)
                                  }
                                />
                              ) : (
                                <ArchiveIcon
                                  style={{
                                    cursor: "pointer",
                                  }}
                                  onClick={() =>
                                    archiveClaimTemplate(claimTemplate.id)
                                  }
                                />
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <Modal
        successModalOpen={showClaimTemplateDeleteModal}
        setSuccessModalOpen={closeClaimTemplateDeleteModal}
        successHeader="Delete Claim Template"
        successContent={modalContent}
        successCallback={deleteClaimTemplate}
        closeCallback={closeClaimTemplateDeleteModal}
        confirmButtonText="Continue"
      />
    </div>
  );
};

export default ClaimTemplateBuilder;
